import { EventEmitter } from '@alphahub/eie-core';
import type { WebFaceDetector } from '../WebFaceDetector';
import type { WebPoseDetector } from '../WebPoseDetector';
import type { WebHandDetector } from '../WebHandDetector';

export interface ITrackingService {
  start: () => void;
  stop: () => void;
}

type DetectorGetters = {
  getFaceDetector: () => WebFaceDetector | undefined;
  getPoseDetector: () => WebPoseDetector | undefined;
  getHandDetector: () => WebHandDetector | undefined;
};

/**
 * TrackingService encapsulates the animation-frame tracking loop that polls
 * detectors and emits landmarks/events. It updates external session state
 * via provided callbacks so `EIESession` keeps owning the timeline/state.
 */
export class TrackingService implements ITrackingService {
  private trackingInterval?: number;
  private frameCount = 0;
  private nullCount = 0;

  constructor(
    config: any,
    private emitter: EventEmitter,
    private getters: DetectorGetters,
    private setters: {
      setLatestLandmarks: (landmarks?: any[]) => void;
      setLatestBlendshapes: (bs?: Record<string, number>) => void;
      setLatestHandLandmarks: (landmarks?: any[]) => void;
      setLastValidFaceDetectionTime: (ts: number) => void;
    },
  ) {
    void config;
  }

  start(): void {
    // guard to avoid duplicate loops
    if (this.trackingInterval) return;

    const tick = async () => {
      const facialDetector = this.getters.getFaceDetector();
      const poseDetector = this.getters.getPoseDetector();
      const handDetector = this.getters.getHandDetector();

      if (facialDetector) {
        try {
          const state = await facialDetector.detect();
          if (state?.faceLandmarks) {
            this.nullCount = 0;
            this.setters.setLatestLandmarks(state.faceLandmarks);
            if (state.blendshapes) this.setters.setLatestBlendshapes(state.blendshapes);
            this.setters.setLastValidFaceDetectionTime(Date.now());
            this.emitter.emit('faceLandmarks', state.faceLandmarks);
            if (this.frameCount++ % 60 === 0) console.log('🎯 Tracking frame', this.frameCount, 'bs:', Object.keys(state.blendshapes || {}).length);
          } else {
            if (this.nullCount++ % 120 === 0) console.warn('👻 Tracking: detect() returned no face, null count:', this.nullCount);
          }
        } catch (err) {
          // swallow detector errors to avoid breaking the loop
        }
      }

      if (poseDetector) {
        try {
          const pose = poseDetector.detect();
          if (pose) {
            this.emitter.emit('bodyPoseLandmarks', pose);
            if (handDetector) {
              try {
                const hd = handDetector.detect();
                if (hd && hd.landmarks) {
                  this.setters.setLatestHandLandmarks(hd.landmarks);
                  this.emitter.emit('handLandmarks', hd.landmarks);
                }
              } catch {}
            }
          }
        } catch {}
      }

      // schedule next frame (guard window existence for non-browser tests)
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        this.trackingInterval = window.requestAnimationFrame(tick) as unknown as number;
      }
    };

    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      this.trackingInterval = window.requestAnimationFrame(tick) as unknown as number;
    }
  }

  stop(): void {
    if (this.trackingInterval && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(this.trackingInterval);
    }
    this.trackingInterval = undefined;
  }
}
